import { BALANCE, TRADE_GOOD_IDS } from '@sd/content';
import type { GameState, PlayerState } from '@sd/sim';

/**
 * Each player's Prestige, wealth and output, sampled once a cycle for the
 * end-of-match charts (#59). Pure: the page records it from snapshots, the
 * worker while it skips ahead.
 */
export type Metric = 'prestige' | 'wealth' | 'output';
export const METRICS: readonly Metric[] = ['prestige', 'wealth', 'output'];

export interface Sample {
  readonly tick: number;
  readonly players: Readonly<Record<string, Readonly<Record<Metric, number>>>>;
}

/** Everything a player owns, in ГС: cash at their rate, goods at the exchange price. */
export function wealthOf(state: GameState, p: PlayerState): number {
  return (
    p.cash * p.currency.rate +
    TRADE_GOOD_IDS.reduce((sum, g) => sum + p.goods[g] * state.market[g].price, 0)
  );
}

/** Last cycle's production at exchange prices, ГС. */
export function outputOf(state: GameState, p: PlayerState): number {
  const c = p.planet.lastCycle;
  return c ? TRADE_GOOD_IDS.reduce((sum, g) => sum + c.produced[g] * state.market[g].price, 0) : 0;
}

export function sample(state: GameState): Sample {
  return {
    tick: state.tick,
    players: Object.fromEntries(
      state.players.map((p) => [
        p.id,
        { prestige: p.prestige, wealth: wealthOf(state, p), output: outputOf(state, p) },
      ]),
    ),
  };
}

/** One sample per cycle, plus the final state when the match ends. */
export class MatchHistory {
  private list: Sample[] = [];

  /** Samples the worker took while it skipped ahead (dev `at=`), before the first snapshot. */
  seed(samples: readonly Sample[]) {
    this.list = [...samples, ...this.list.filter((x) => x.tick > (samples.at(-1)?.tick ?? -1))];
  }

  get samples(): readonly Sample[] {
    return this.list;
  }

  record(state: GameState) {
    const last = this.list.at(-1);
    const cycle = Math.floor(state.tick / BALANCE.cycleTicks);
    const lastCycle = last ? Math.floor(last.tick / BALANCE.cycleTicks) : -1;
    if (cycle > lastCycle || (state.result && last?.tick !== state.tick)) {
      this.list.push(sample(state));
    }
  }
}
